import React from 'react';
import MaterialTable from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import SendIcon from '@material-ui/icons/Send';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

const MENU_ICON_STYLE = { minWidth: 32, color: '#6b7280' };

// Unverified is a pending state, not a failure: amber, never red.
const STATUS_BADGE_CLASS = {
  verified: 'badge-success',
  unverified: 'badge-warning',
};

const TABLE_HEADER_STYLE = {
  backgroundColor: '#f7f9fc',
  color: '#6b7280',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  padding: '12px 16px',
  borderTop: '1px solid #edf0f5',
  borderBottom: '1px solid #edf0f5',
  whiteSpace: 'nowrap',
};

const TABLE_CELL_STYLE = {
  padding: '12px 16px',
  fontSize: '0.88rem',
  color: '#1f2937',
};

const EMPTY = <span className="support-services-card__empty">—</span>;

export default class CompaniesDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.searchTimer = null;

    this.columns = [
      {
        title: this.label('name', 'Company'),
        field: 'name',
        orderField: 'name',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 240 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 240 },
        render: rowData => (
          <div className="companies-table__cell">
            <a href={rowData.company_url} className="ehc-service-orders-card__link">
              {rowData.name}
            </a>
            <span className="companies-table__subline">{rowData.relation_label}</span>
          </div>
        ),
      },
      {
        title: this.label('status', 'Status'),
        field: 'human_status',
        orderField: 'status',
        cellStyle: { ...TABLE_CELL_STYLE, width: 140 },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 140 },
        render: rowData => (
          <span className={`badge ${STATUS_BADGE_CLASS[rowData.status] || 'badge-secondary'}`}>
            {rowData.human_status}
          </span>
        ),
      },
      {
        title: this.label('contact', 'Contact'),
        field: 'email',
        orderField: 'email',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 220 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 220 },
        render: rowData => (
          <div className="companies-table__cell">
            <span>{rowData.email || EMPTY}</span>
            {rowData.phone_number && (
              <span className="companies-table__subline">{rowData.phone_number}</span>
            )}
          </div>
        ),
      },
      {
        title: this.label('pic_account', 'PIC account'),
        field: 'account',
        sorting: false,
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 180 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 180 },
        render: rowData => (
          <div className="companies-table__cell">
            <span>{rowData.account || EMPTY}</span>
            {rowData.pic_position && (
              <span className="companies-table__subline">{rowData.pic_position}</span>
            )}
          </div>
        ),
      },
      {
        title: this.label('created_at', 'Created'),
        field: 'created_at',
        orderField: 'created_at',
        searchable: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 130, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 130 },
      },
      {
        title: '',
        sorting: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 56, textAlign: 'right' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 56 },
        render: rowData => (
          <button
            type="button"
            className="companies-table__kebab"
            aria-label={this.label('actions', 'Actions')}
            onClick={(event) => this.openActions(event, rowData)}
          >
            <MoreVertIcon fontSize="small" />
          </button>
        ),
      },
    ];

    this.state = {
      statusFilter: '',
      relationFilter: '',
      statusCounts: {},
      searchInput: '',
      searchQuery: '',
      actionAnchor: null,
      actionRow: null,
    };
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  label(key, fallback) {
    return this.props.labels?.[key] || fallback;
  }

  getTheme = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';
    return createTheme({
      palette: {
        type: isDarkMode ? 'dark' : 'light',
        primary: { main: '#41924E' },
        secondary: { main: '#2e7d38' },
        background: {
          paper: isDarkMode ? '#2a3441' : '#ffffff',
          default: isDarkMode ? '#1e2530' : '#f7f9fc',
        },
      },
      overrides: {
        MuiTableCell: {
          root: {
            borderBottom: isDarkMode ? '1px solid #3a4757' : '1px solid #f0f2f6',
          },
        },
      },
    });
  };

  fetchData = (query) => new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      page: query.page + 1,
      per_page: query.pageSize,
      q: this.state.searchQuery || '',
      sort_by: query.orderBy?.orderField ?? query.orderBy?.field ?? 'created_at',
      sort_order: query.orderDirection ?? 'desc',
    });

    if (this.state.statusFilter) params.set('status', this.state.statusFilter);
    if (this.state.relationFilter) params.set('relation', this.state.relationFilter);

    fetch(`/api/companies?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load companies');
        return res.json();
      })
      .then((data) => {
        this.setState({ statusCounts: data.status_counts || {} });
        resolve({
          data: data.companies,
          page: data.pagination.page - 1,
          totalCount: data.pagination.count,
        });
      })
      .catch(reject);
  });

  refreshTable = () => {
    if (this.tableRef.current) this.tableRef.current.onQueryChange({ page: 0 });
  };

  openActions = (event, rowData) => {
    event.stopPropagation();
    this.setState({ actionAnchor: event.currentTarget, actionRow: rowData });
  };

  closeActions = () => this.setState({ actionAnchor: null, actionRow: null });

  handleStatusFilter = (status) => {
    this.setState({ statusFilter: status }, this.refreshTable);
  };

  handleRelationFilter = (event) => {
    this.setState({ relationFilter: event.target.value }, this.refreshTable);
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ searchInput: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.setState({ searchQuery: value.trim() }, this.refreshTable);
    }, 350);
  };

  clearSearch = () => {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.setState({ searchInput: '', searchQuery: '' }, this.refreshTable);
  };

  renderStatusFilters = () => {
    const { statusCounts, statusFilter } = this.state;
    const total = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
    const filters = [
      { key: '', label: this.label('all_statuses', 'All'), count: total },
      { key: 'verified', label: this.label('status_verified', 'Verified'), count: statusCounts.verified || 0 },
      { key: 'unverified', label: this.label('status_unverified', 'Unverified'), count: statusCounts.unverified || 0 },
    ];

    return (
      <div className="ehc-service-orders-card__filters" role="group" aria-label={this.label('status', 'Status')}>
        {filters.map(({ key, label, count }) => (
          <button
            key={key || 'all'}
            type="button"
            className={`ehc-service-orders-card__filter-pill${statusFilter === key ? ' ehc-service-orders-card__filter-pill--active' : ''}`}
            onClick={() => this.handleStatusFilter(key)}
          >
            {label}
            <span className="ehc-service-orders-card__filter-count">{count}</span>
          </button>
        ))}
      </div>
    );
  };

  // Row actions are plain anchors so rails-ujs keeps handling remote/method/confirm.
  renderActionsMenu = () => {
    const row = this.state.actionRow;
    if (!row) return null;

    const verifyLabel = row.status === 'verified'
      ? this.label('action_unverify', 'Revoke verification')
      : this.label('action_verify', 'Verify company');

    return (
      <Menu
        anchorEl={this.state.actionAnchor}
        open={Boolean(this.state.actionAnchor)}
        onClose={this.closeActions}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ style: { minWidth: 230 } }}
      >
        <MenuItem component="a" href={row.company_url} onClick={this.closeActions}>
          <ListItemIcon style={MENU_ICON_STYLE}><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={this.label('action_show', 'View detail')} />
        </MenuItem>
        {this.props.updatable && [
          <MenuItem
            key="edit"
            component="a"
            href={row.edit_url}
            className="modal-link"
            data-remote="true"
            onClick={this.closeActions}
          >
            <ListItemIcon style={MENU_ICON_STYLE}><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={this.label('action_edit', 'Edit data')} />
          </MenuItem>,
          <MenuItem
            key="login-email"
            component="a"
            href={row.change_login_email_url}
            className="modal-link"
            data-remote="true"
            onClick={this.closeActions}
          >
            <ListItemIcon style={MENU_ICON_STYLE}><MailOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={this.label('action_login_email', 'Change login email')} />
          </MenuItem>,
          <MenuItem
            key="notification-settings"
            component="a"
            href={row.notification_settings_url}
            className="modal-link"
            data-remote="true"
            onClick={this.closeActions}
          >
            <ListItemIcon style={MENU_ICON_STYLE}><NotificationsNoneIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={this.label('action_notification_settings', 'Notification settings')} />
          </MenuItem>,
          <Divider key="divider-1" />,
          <MenuItem
            key="status"
            component="a"
            href={row.status_toggle_url}
            data-method="post"
            data-confirm={`${verifyLabel}?`}
            onClick={this.closeActions}
          >
            <ListItemIcon style={MENU_ICON_STYLE}><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={verifyLabel} />
          </MenuItem>,
        ]}
        {this.props.updatable && row.invitation_pending && (
          <MenuItem
            component="a"
            href={row.resend_invitation_url}
            data-method="post"
            onClick={this.closeActions}
          >
            <ListItemIcon style={MENU_ICON_STYLE}><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={this.label('action_resend_invitation', 'Resend invitation')} />
          </MenuItem>
        )}
        {this.props.deletable && <Divider />}
        {this.props.deletable && (
          <MenuItem
            component="a"
            href={row.company_url}
            data-method="delete"
            data-confirm={row.destroy_data.confirm}
            data-title={row.destroy_data.title}
            data-commit={row.destroy_data.commit}
            data-commit-class={row.destroy_data['commit-class']}
            data-verify={row.destroy_data.verify}
            data-verify-text={row.destroy_data['verify-text']}
            onClick={this.closeActions}
          >
            <ListItemIcon style={{ minWidth: 32, color: '#dc3545' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={this.label('action_destroy', 'Delete company')}
              primaryTypographyProps={{ style: { color: '#dc3545' } }}
            />
          </MenuItem>
        )}
      </Menu>
    );
  };

  renderToolbar = () => (
    <div className="benefit-codes-card__header ehc-service-orders-card__header">
      {this.renderStatusFilters()}
      <div className="benefit-codes-card__actions">
        <div className="form-group companies-table__relation-filter mb-0">
          <select
            className="form-control"
            value={this.state.relationFilter}
            onChange={this.handleRelationFilter}
            aria-label={this.label('relation', 'Relation')}
          >
            <option value="">{this.label('all_relations', 'All relations')}</option>
            <option value="parent">{this.label('parent', 'Holding')}</option>
            <option value="child">{this.label('child', 'Affiliate')}</option>
            <option value="standalone">{this.label('relation_standalone', 'Standalone company')}</option>
          </select>
          <i className="form-group__bar" />
        </div>
        <div className="benefit-codes-card__search">
          <SearchIcon className="benefit-codes-card__search-icon" fontSize="small" />
          <input
            type="text"
            className="benefit-codes-card__search-input"
            placeholder={this.label('search_placeholder', 'Search companies...')}
            value={this.state.searchInput}
            onChange={this.handleSearchChange}
          />
          {this.state.searchInput && (
            <button
              type="button"
              className="benefit-codes-card__search-clear"
              onClick={this.clearSearch}
              aria-label={this.label('clear_search', 'Clear search')}
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="benefit-codes-card__btn benefit-codes-card__btn--icon"
          onClick={this.refreshTable}
          aria-label={this.label('refresh', 'Refresh Data')}
          title={this.label('refresh', 'Refresh Data')}
        >
          <RefreshIcon fontSize="small" />
        </button>
        {this.props.creatable && this.props.invite_modal_id && (
          <a
            href={`#${this.props.invite_modal_id}`}
            className="benefit-codes-card__btn benefit-codes-card__btn--primary"
            data-toggle="modal"
            title={this.label('new_tooltip', 'Add new company')}
          >
            <AddIcon fontSize="small" />
            <span>{this.label('new', 'Add Company')}</span>
          </a>
        )}
      </div>
    </div>
  );

  render() {
    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <div className="benefit-codes-card ehc-service-orders-card">
          {this.renderToolbar()}

          <div className="benefit-codes-card__table-wrap ehc-service-orders-card__table-wrap">
            <MaterialTable
              title=""
              columns={this.columns}
              tableRef={this.tableRef}
              data={this.fetchData}
              onRowClick={(_, rowData) => {
                if (rowData?.company_url) window.location.href = rowData.company_url;
              }}
              localization={{
                body: { emptyDataSourceMessage: this.label('empty', 'No companies found') },
                pagination: { labelRowsSelect: this.label('rows', 'rows') },
              }}
              options={{
                pageSize: 20,
                pageSizeOptions: [20, 30, 50],
                exportButton: false,
                sorting: true,
                search: false,
                toolbar: false,
                grouping: false,
                emptyRowsWhenPaging: false,
                paginationType: 'normal',
                padding: 'default',
                headerStyle: TABLE_HEADER_STYLE,
                rowStyle: { cursor: 'pointer', fontSize: '0.88rem' },
              }}
              style={{ boxShadow: 'none', background: 'transparent' }}
            />
          </div>

          {this.renderActionsMenu()}
        </div>
      </MuiThemeProvider>
    );
  }
}
